import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Sparkles, 
  Bot, User, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { getApiBase } from '../utils';

const API_URL = getApiBase();

const AIAssistant = ({ onDismiss }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your fashionDisc Guide Agent ✨ I can help you navigate the app, explain features, and answer any questions about what you can do here. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatOverlayRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for other chat agents opening - close this one
  useEffect(() => {
    const handleOtherChatOpen = (e) => {
      if (e.detail?.agent !== 'guide') {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('chatAgentOpened', handleOtherChatOpen);
    return () => window.removeEventListener('chatAgentOpened', handleOtherChatOpen);
  }, []);

  // Broadcast when this chat opens or closes
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('chatAgentOpened', { detail: { agent: 'guide', isOpen: true } }));
    } else {
      window.dispatchEvent(new CustomEvent('chatAgentClosed', { detail: { agent: 'guide', isOpen: false } }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Lock scroll + reposition overlay when iOS keyboard opens
  useEffect(() => {
    if (!isOpen) return;
    const prevBg = document.body.style.backgroundColor;
    const prevHtmlBg = document.documentElement.style.backgroundColor;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';
    document.documentElement.style.backgroundColor = '#ffffff';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.backgroundColor = '#ffffff';

    const repositionOverlay = () => {
      const vv = window.visualViewport;
      const el = chatOverlayRef.current;
      if (!vv || !el) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
      el.style.bottom = 'auto';
    };
    window.visualViewport?.addEventListener('resize', repositionOverlay);
    window.visualViewport?.addEventListener('scroll', repositionOverlay);

    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.documentElement.style.backgroundColor = prevHtmlBg;
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.backgroundColor = prevBg;
      window.visualViewport?.removeEventListener('resize', repositionOverlay);
      window.visualViewport?.removeEventListener('scroll', repositionOverlay);
      const el = chatOverlayRef.current;
      if (el) { el.style.top = ''; el.style.height = ''; el.style.bottom = ''; }
    };
  }, [isOpen]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          message: userMessage,
          history: messages.slice(-10)
        })
      });

      const data = await response.json();
      
      if (response.status === 401) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "Please log in to chat with me! Create an account or sign in to get started. 👗" 
        }]);
      } else if (response.status === 403) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "You've reached your chat limit for this month. Upgrade your plan for more AI chats! ✨",
          upgradeNeeded: true
        }]);
      } else if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm having trouble connecting right now. Please try again in a moment!" 
        }]);
      }
    } catch (error) {
      console.error('Assistant error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment!" 
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickQuestions = [
    "What can I do here?",
    "Style Studio?",
    "AI Studio?",
    "My Wardrobe?"
  ];

  return (
    <>
      {/* ========== GUIDE AGENT (Portal to body root) ========== */}
      {createPortal(<>
      {/* Floating Button — fixed bottom-right, above tab bar */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed z-[45]"
            style={{ 
              bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
              right: '16px'
            }}
          >
            <button
              onClick={() => setIsOpen(true)}
              className="relative w-12 h-12 bg-gradient-to-r from-terracotta-600 to-suede-600 rounded-full shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
              aria-label="Open Guide Agent"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-olive-500 rounded-full border-2 border-white animate-pulse" />
            </button>

            {/* Dismiss button — small X top-right of the bubble */}
            {onDismiss && (
              <button
                onClick={(e) => { e.stopPropagation(); onDismiss('guide'); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-500/80 rounded-full flex items-center justify-center shadow active:scale-90 transition-transform"
                aria-label="Hide Guide Agent"
              >
                <X className="w-2.5 h-2.5 text-white" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Panel — Full Page */}
      <AnimatePresence>
        {isOpen && (
            <motion.div
              ref={chatOverlayRef}
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-0 z-[100] bg-white flex flex-col"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-terracotta-600 to-suede-600 flex-shrink-0" style={{ paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))' }}>
                <div className="flex items-center justify-between px-4 pb-3 pt-1">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-white font-semibold text-base">fashionDisc Guide</span>
                      <p className="text-white/70 text-xs">Your app assistant</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="p-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Close chat"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div 
                className="overflow-y-auto p-4 space-y-3 bg-gray-50 flex-1"
              >
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' 
                        ? 'bg-suede-600' 
                        : 'bg-gradient-to-r from-terracotta-500 to-suede-500'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-3.5 h-3.5 text-white" />
                      ) : (
                        <Bot className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <div className={`max-w-[80%] p-3 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-suede-600 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.upgradeNeeded && (
                        <button
                          onClick={() => { setIsOpen(false); window.location.href = '/account'; }}
                          className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-terracotta-500 to-suede-500 text-white text-xs font-semibold rounded-lg active:scale-95 transition-transform"
                        >
                          👑 Upgrade Plan
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-r from-terracotta-500 to-suede-500 flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100">
                      <Loader2 className="w-4 h-4 text-terracotta-600 animate-spin" />
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Questions — only when few messages */}
              {messages.length <= 2 && (
                <div className="px-4 py-2.5 border-t border-gray-100 bg-white flex-shrink-0">
                  <div className="flex flex-wrap gap-2">
                    {quickQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setInput(q);
                          setTimeout(() => handleSend(), 100);
                        }}
                        className="text-xs px-3 py-2 bg-suede-50 text-suede-700 rounded-full hover:bg-suede-100 active:bg-suede-200 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-3 border-t border-gray-200 bg-white flex-shrink-0" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything..."
                    className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-suede-500 focus:bg-white transition-all"
                    style={{ fontSize: '16px' }}
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    className="w-11 h-11 bg-gradient-to-r from-terracotta-600 to-suede-600 rounded-xl flex items-center justify-center text-white disabled:opacity-50 active:scale-95 transition-transform flex-shrink-0"
                    aria-label="Send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
        )}
      </AnimatePresence>
      </>, document.body)}
    </>
  );
};

export default AIAssistant;
